import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import {
  UploadCloud, Loader2, FileText, CheckCircle2, XCircle, Trash2, Eye,
  Sparkles, RefreshCw, AlertTriangle, UserPlus, UserCheck
} from 'lucide-react';
import { capturaFacturasAPI, fiscalidadAPI } from '../api';
import { toast } from '../hooks/use-toast';

const eur = (n) => new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(Number(n || 0));

const ESTADO_BADGE = {
  procesando: { label: 'Procesando…', cls: 'bg-amber-100 text-amber-800' },
  revision: { label: 'Por revisar', cls: 'bg-blue-100 text-blue-800' },
  error: { label: 'Error', cls: 'bg-red-100 text-red-800' },
};

const CapturaFacturasDialog = ({ open, onOpenChange, onConfirmado }) => {
  const [capturas, setCapturas] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [codigosAEAT, setCodigosAEAT] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  // Facturas duplicadas que el usuario ya ha visto avisadas una vez
  const [duplicadoVisto, setDuplicadoVisto] = useState({});
  const fileRef = useRef(null);
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await capturaFacturasAPI.list();
      setCapturas(r.data || []);
      return r.data || [];
    } catch (e) { return []; }
  }, []);

  useEffect(() => {
    if (open) {
      load();
      fiscalidadAPI.getCodigosAEAT().then(r => setCodigosAEAT(r.data || [])).catch(() => {});
    }
  }, [open, load]);

  // Polling mientras haya facturas en procesamiento
  useEffect(() => {
    if (!open) return;
    const hayProcesando = capturas.some(c => c.estado === 'procesando');
    if (hayProcesando) {
      pollRef.current = setTimeout(() => { load(); }, 3000);
    }
    return () => pollRef.current && clearTimeout(pollRef.current);
  }, [open, capturas, load]);

  const handleFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    const fd = new FormData();
    files.forEach(f => fd.append('files', f));
    setUploading(true);
    try {
      const r = await capturaFacturasAPI.upload(fd);
      toast({ title: 'Facturas subidas', description: `${r.data.total} archivo(s) en análisis por IA…` });
      await load();
    } catch (e) {
      toast({ title: 'Error al subir', description: e?.response?.data?.detail || 'Revisa el formato (PDF, JPG, PNG)', variant: 'destructive' });
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const patch = (id, campo, valor) => {
    setCapturas(prev => prev.map(c => c.id === id ? { ...c, [campo]: valor } : c));
  };

  const guardar = async (c) => {
    const payload = {
      proveedor_nombre: c.proveedor_nombre, proveedor_nif: c.proveedor_nif,
      proveedor_direccion: c.proveedor_direccion, proveedor_cp: c.proveedor_cp,
      proveedor_ciudad: c.proveedor_ciudad, proveedor_provincia: c.proveedor_provincia,
      numero_factura: c.numero_factura, fecha: c.fecha, descripcion: c.descripcion,
      importe_base: parseFloat(c.importe_base) || 0,
      porcentaje_iva: parseFloat(c.porcentaje_iva) || 0,
      porcentaje_retencion: parseFloat(c.porcentaje_retencion) || 0,
      codigo_aeat: c.codigo_aeat, deducible_iva: c.deducible_iva, deducible_irpf: c.deducible_irpf,
      porcentaje_afectacion: parseFloat(c.porcentaje_afectacion) || 100,
      tiene_factura_completa: c.tiene_factura_completa, es_bien_inversion: c.es_bien_inversion,
    };
    const r = await capturaFacturasAPI.update(c.id, payload);
    return r.data;
  };

  const confirmar = async (c) => {
    if (c.tiene_factura_completa !== false && !(c.numero_factura || '').trim()) {
      toast({
        title: 'Falta el Nº de factura',
        description: 'Es imprescindible para justificar el gasto. Escríbelo arriba o desactiva «Factura completa» si es un ticket.',
        variant: 'destructive'
      });
      return;
    }
    if (c.duplicado && !duplicadoVisto[c.id]) {
      setDuplicadoVisto(prev => ({ ...prev, [c.id]: true }));
      const ya = c.duplicado.gastos?.[0];
      toast({
        title: 'Posible factura duplicada',
        description: ya
          ? `Ya hay un gasto con la factura ${ya.numero_factura || ''} de ${ya.proveedor_nombre || 'ese proveedor'} (${ya.fecha || ''}). No se ha creado nada: pulsa otra vez «Confirmar» si de verdad es otra factura, o descártala.`
          : 'Esta factura ya está registrada. Pulsa otra vez «Confirmar» para crearla igualmente.',
        variant: 'destructive'
      });
      return;
    }
    try {
      await guardar(c);
      const crearProv = !c.proveedor_id;
      const r = await capturaFacturasAPI.confirmar(c.id, { crear_proveedor: crearProv });
      toast({
        title: 'Gasto creado', variant: 'default',
        description: r.data.proveedor_creado ? 'Proveedor dado de alta en Contactos y gasto tramitado.' : 'Factura tramitada como gasto.',
      });
      await load();
      onConfirmado && onConfirmado();
    } catch (e) {
      toast({ title: 'No se pudo confirmar', description: e?.response?.data?.detail || 'Error', variant: 'destructive' });
    }
  };

  const descartar = async (id) => {
    await capturaFacturasAPI.descartar(id);
    await load();
  };
  const reintentar = async (id) => {
    await capturaFacturasAPI.reintentar(id);
    await load();
  };
  const eliminar = async (id) => {
    await capturaFacturasAPI.eliminar(id);
    await load();
  };

  const recalc = (c) => {
    const base = parseFloat(c.importe_base) || 0;
    const iva = base * (parseFloat(c.porcentaje_iva) || 0) / 100;
    const ret = base * (parseFloat(c.porcentaje_retencion) || 0) / 100;
    return base + iva - ret;
  };

  const gastosCodigos = codigosAEAT.filter(x => x.tipo === 'gasto');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="captura-facturas-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-indigo-600" /> Importar facturas con IA (OCR)
          </DialogTitle>
        </DialogHeader>

        {/* Zona de subida */}
        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => { e.preventDefault(); setDragOver(false); handleFiles(e.dataTransfer.files); }}
          className={`border-2 border-dashed rounded-xl p-6 text-center transition-colors ${dragOver ? 'border-indigo-500 bg-indigo-50' : 'border-slate-300 bg-slate-50/60'}`}
          data-testid="captura-dropzone"
        >
          <UploadCloud className="w-10 h-10 mx-auto text-indigo-500 mb-2" />
          <p className="text-sm text-slate-600 mb-3">Arrastra aquí tus facturas (PDF, JPG o PNG) o súbelas una a una o en grupo.</p>
          <input ref={fileRef} type="file" accept=".pdf,.png,.jpg,.jpeg,.webp" multiple hidden
                 onChange={(e) => handleFiles(e.target.files)} data-testid="captura-file-input" />
          <Button onClick={() => fileRef.current?.click()} disabled={uploading}
                  className="bg-indigo-600 hover:bg-indigo-700" data-testid="captura-seleccionar-btn">
            {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <UploadCloud className="w-4 h-4 mr-2" />}
            Seleccionar archivos
          </Button>
          <p className="text-[11px] text-slate-400 mt-2">La IA lee cada factura y propone los datos. Nada se guarda hasta que tú lo confirmas.</p>
        </div>

        {/* Bandeja de revisión */}
        <div className="space-y-4 mt-2" data-testid="captura-bandeja">
          {capturas.length === 0 && (
            <p className="text-center text-sm text-slate-400 py-6">No hay facturas pendientes de revisar.</p>
          )}
          {capturas.map((c) => {
            const badge = ESTADO_BADGE[c.estado] || ESTADO_BADGE.revision;
            return (
              <div key={c.id} className="border rounded-lg p-4 bg-white" data-testid={`captura-item-${c.id}`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2 min-w-0">
                    <FileText className="w-4 h-4 text-slate-400 shrink-0" />
                    <span className="text-sm font-medium truncate">{c.nombre_archivo}</span>
                    <Badge className={badge.cls}>{badge.label}</Badge>
                    {c.estado === 'revision' && c.confianza > 0 && (
                      <span className="text-xs text-slate-400">confianza {(c.confianza * 100).toFixed(0)}%</span>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    <Button size="sm" variant="ghost" onClick={() => window.open(capturaFacturasAPI.previewUrl(c.id), '_blank')} title="Ver archivo">
                      <Eye className="w-4 h-4" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => eliminar(c.id)} title="Eliminar">
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                </div>

                {c.estado === 'procesando' && (
                  <div className="flex items-center gap-2 text-sm text-amber-700 py-3">
                    <Loader2 className="w-4 h-4 animate-spin" /> La IA está leyendo la factura…
                  </div>
                )}

                {c.estado === 'error' && (
                  <div className="flex items-center justify-between text-sm text-red-700 py-2">
                    <span className="flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> {c.mensaje_error || 'No se pudo procesar'}</span>
                    <Button size="sm" variant="outline" onClick={() => reintentar(c.id)}>
                      <RefreshCw className="w-3 h-3 mr-1" /> Reintentar
                    </Button>
                  </div>
                )}

                {c.estado === 'revision' && (
                  <div className="space-y-4">
                    {/* Proveedor */}
                    <div className="rounded-md border bg-slate-50/60 p-3">
                      <div className="flex items-center gap-2 mb-2">
                        {c.proveedor_id
                          ? <Badge className="bg-emerald-100 text-emerald-800"><UserCheck className="w-3 h-3 mr-1" />Proveedor existente</Badge>
                          : <Badge className="bg-orange-100 text-orange-800"><UserPlus className="w-3 h-3 mr-1" />Proveedor nuevo (se creará al confirmar)</Badge>}
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                        <div className="col-span-2"><Label className="text-xs">Nombre / Razón social</Label>
                          <Input value={c.proveedor_nombre || ''} onChange={e => patch(c.id, 'proveedor_nombre', e.target.value)} data-testid={`cap-prov-nombre-${c.id}`} /></div>
                        <div><Label className="text-xs">NIF/CIF</Label>
                          <Input value={c.proveedor_nif || ''} onChange={e => patch(c.id, 'proveedor_nif', e.target.value)} data-testid={`cap-prov-nif-${c.id}`} /></div>
                        <div><Label className="text-xs">Teléfono</Label>
                          <Input value={c.proveedor_telefono || ''} onChange={e => patch(c.id, 'proveedor_telefono', e.target.value)} /></div>
                        <div className="col-span-2"><Label className="text-xs">Dirección</Label>
                          <Input value={c.proveedor_direccion || ''} onChange={e => patch(c.id, 'proveedor_direccion', e.target.value)} /></div>
                        <div><Label className="text-xs">C.P.</Label>
                          <Input value={c.proveedor_cp || ''} onChange={e => patch(c.id, 'proveedor_cp', e.target.value)} /></div>
                        <div><Label className="text-xs">Ciudad</Label>
                          <Input value={c.proveedor_ciudad || ''} onChange={e => patch(c.id, 'proveedor_ciudad', e.target.value)} /></div>
                      </div>
                    </div>

                    {c.duplicado && (
                      <div className="rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-800" data-testid={`cap-duplicado-${c.id}`}>
                        <div className="flex items-center gap-2 font-medium">
                          <AlertTriangle className="w-4 h-4" /> Esta factura ya está registrada como gasto
                        </div>
                        <ul className="mt-1 space-y-0.5 text-xs">
                          {(c.duplicado.gastos || []).map(d => (
                            <li key={d.id}>
                              nº {d.numero_factura || 'sin nº'} · {d.proveedor_nombre || 'sin proveedor'} · {d.fecha || ''} · {eur(d.coste_total)}
                              {d.coincidencia === 'total' && <span className="font-medium"> (mismo importe)</span>}
                            </li>
                          ))}
                        </ul>
                        <p className="mt-1 text-xs">Descártala para no duplicar el gasto, o confírmala dos veces si de verdad es otra factura.</p>
                      </div>
                    )}

                    {/* Factura */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                      <div><Label className="text-xs">Nº factura{c.tiene_factura_completa === false ? '' : ' *'}</Label>
                        <Input value={c.numero_factura || ''} onChange={e => patch(c.id, 'numero_factura', e.target.value)}
                          className={c.tiene_factura_completa !== false && !(c.numero_factura || '').trim() ? 'border-red-400' : ''}
                          data-testid={`cap-num-${c.id}`} />
                        {c.tiene_factura_completa !== false && !(c.numero_factura || '').trim() && (
                          <p className="text-[11px] text-red-600 mt-1" data-testid={`cap-num-aviso-${c.id}`}>La IA no lo ha encontrado: complétalo para poder confirmar</p>
                        )}</div>
                      <div><Label className="text-xs">Fecha</Label>
                        <Input type="date" value={c.fecha || ''} onChange={e => patch(c.id, 'fecha', e.target.value)} data-testid={`cap-fecha-${c.id}`} /></div>
                      <div><Label className="text-xs">Base imponible</Label>
                        <Input type="number" step="0.01" value={c.importe_base ?? 0} onChange={e => patch(c.id, 'importe_base', e.target.value)} data-testid={`cap-base-${c.id}`} /></div>
                      <div><Label className="text-xs">% IVA</Label>
                        <Input type="number" step="0.01" value={c.porcentaje_iva ?? 21} onChange={e => patch(c.id, 'porcentaje_iva', e.target.value)} data-testid={`cap-iva-${c.id}`} /></div>
                      <div><Label className="text-xs">% Retención</Label>
                        <Input type="number" step="0.01" value={c.porcentaje_retencion ?? 0} onChange={e => patch(c.id, 'porcentaje_retencion', e.target.value)} data-testid={`cap-ret-${c.id}`} /></div>
                      <div className="col-span-3"><Label className="text-xs">Descripción</Label>
                        <Input value={c.descripcion || ''} onChange={e => patch(c.id, 'descripcion', e.target.value)} /></div>
                    </div>

                    {/* Clasificación fiscal */}
                    <div className="rounded-md border border-indigo-100 bg-indigo-50/40 p-3 space-y-3">
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                        <div>
                          <Label className="text-xs">Código AEAT</Label>
                          <Select value={c.codigo_aeat || ''} onValueChange={v => patch(c.id, 'codigo_aeat', v)}>
                            <SelectTrigger data-testid={`cap-aeat-${c.id}`}><SelectValue placeholder="Código AEAT" /></SelectTrigger>
                            <SelectContent>
                              {gastosCodigos.map(x => <SelectItem key={x.codigo} value={x.codigo}>{x.codigo} · {x.nombre}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                        <div><Label className="text-xs">% Afectación</Label>
                          <Input type="number" min="0" max="100" value={c.porcentaje_afectacion ?? 100} onChange={e => patch(c.id, 'porcentaje_afectacion', e.target.value)} data-testid={`cap-afect-${c.id}`} /></div>
                        <div className="flex items-end"><span className="text-sm font-semibold">Total: {eur(recalc(c))}</span></div>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                        <label className="flex items-center gap-2 text-sm"><Switch checked={!!c.deducible_iva} onCheckedChange={v => patch(c.id, 'deducible_iva', v)} data-testid={`cap-ded-iva-${c.id}`} /> Deducible IVA</label>
                        <label className="flex items-center gap-2 text-sm"><Switch checked={!!c.deducible_irpf} onCheckedChange={v => patch(c.id, 'deducible_irpf', v)} /> Deducible IRPF</label>
                        <label className="flex items-center gap-2 text-sm"><Switch checked={!!c.tiene_factura_completa} onCheckedChange={v => patch(c.id, 'tiene_factura_completa', v)} /> Factura completa</label>
                        <label className="flex items-center gap-2 text-sm"><Switch checked={!!c.es_bien_inversion} onCheckedChange={v => patch(c.id, 'es_bien_inversion', v)} data-testid={`cap-bien-inv-${c.id}`} /> Bien de inversión</label>
                      </div>
                      {c.justificacion_ia && (
                        <p className="text-[11px] text-indigo-700 flex items-start gap-1"><Sparkles className="w-3 h-3 mt-0.5 shrink-0" /> {c.justificacion_ia}</p>
                      )}
                    </div>

                    <div className="flex justify-end gap-2">
                      <Button size="sm" variant="outline" onClick={() => descartar(c.id)} data-testid={`cap-descartar-${c.id}`}>
                        <XCircle className="w-4 h-4 mr-1" /> Descartar
                      </Button>
                      <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => confirmar(c)} data-testid={`cap-confirmar-${c.id}`}>
                        <CheckCircle2 className="w-4 h-4 mr-1" /> Confirmar y crear gasto
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CapturaFacturasDialog;
